import React from 'react';
import PaymentUpiQrCode from './PaymentUpiQrCode';
import { PAYMENT_MERCHANT } from '../config/paymentMerchant';
import { useUpiPaymentLauncher } from '../hooks/useUpiPaymentLauncher';

/**
 * Crystal-clear mobile pay: download QR → open any UPI app → Gallery.
 * (Browser upi:// intent fails for Axis merchant VPA; QR path works.)
 */
function MobilePaymentMethodChooser({
  displayAmount,
  planName,
  launcher,
  className = '',
}) {
  const internal = useUpiPaymentLauncher({ displayAmount, planName });
  const { showUploadReminder } = launcher || internal;

  return (
    <div className={`space-y-4 text-left ${className}`.trim()}>
      <div className="text-center">
        <p className="text-[11px] font-bold uppercase tracking-widest text-indigo-600">
          Pay with any UPI app
        </p>
        <h3 className="mt-1 text-base font-black text-slate-900 tracking-tight">
          GPay · PhonePe · Paytm · BHIM
        </h3>
        <p className="mt-1.5 text-xs text-slate-600 leading-snug max-w-sm mx-auto">
          Pay <strong className="text-slate-900">{displayAmount}</strong> to{' '}
          <strong className="text-slate-900">{PAYMENT_MERCHANT.payeeName}</strong>
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <PaymentUpiQrCode
          displayAmount={displayAmount}
          planName={planName}
          size={200}
          showAmountHint={false}
          showDownload
          downloadButtonLabel="Download QR to pay with any UPI app"
          downloadButtonPrimary
        />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">
          3 easy steps (same phone)
        </p>
        <ol className="space-y-2 text-sm text-slate-800 leading-snug">
          <li className="flex gap-3">
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-black text-white">
              1
            </span>
            <span>
              Tap <strong>Download QR to pay with any UPI app</strong> (saves the QR image).
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-black text-white">
              2
            </span>
            <span>
              Open <strong>GPay / PhonePe / Paytm</strong> → <strong>Scan</strong> →{' '}
              <strong>Gallery</strong> → select that image.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-black text-white">
              3
            </span>
            <span>
              Pay, then come back and tap <strong>Proceed to Upload Payment Proof</strong>.
            </span>
          </li>
        </ol>
      </div>

      <p className="text-center text-[11px] text-slate-500 leading-snug">
        Someone else can also scan this QR with their phone — no download needed.
      </p>

      {showUploadReminder && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-sm font-semibold text-emerald-900 leading-snug">
            Payment done? Upload your proof below so we can verify it.
          </p>
        </div>
      )}
    </div>
  );
}

export default MobilePaymentMethodChooser;
